import { signInWithEmailAndPassword, signOut } from 'firebase/auth'
import { firebaseAuth } from '@/firebase'
import { reactive } from 'vue'
import { resetAllComposables } from '@/composables/stateReset'

const state = reactive({
  user: null,
  jwtData: null,
  isLoggingOut: false,
  lastTokenRefresh: null,
})

export default function useAuth() {
  const login = async (email, password) => signInWithEmailAndPassword(firebaseAuth, email, password)

  const logout = async () => {
    // Prevent multiple concurrent logout attempts
    if (state.isLoggingOut) {
      console.log('Logout already in progress, skipping...')

      return
    }

    state.isLoggingOut = true

    try {
      // Clear localStorage items that might cause cache issues
      localStorage.removeItem('selectedAcademy')
      localStorage.removeItem('perform72-contentScope')

      // Clear any Vue/Firebase specific cache
      const keys = Object.keys(localStorage)
      keys.forEach(key => {
        if (key.startsWith('firebase:') || key.startsWith('perform72-')) {
          localStorage.removeItem(key)
        }
      })

      // Clear auth state
      state.user = null
      state.jwtData = null
      state.lastTokenRefresh = null

      // Reset composables state
      resetAllComposables()

      // Sign out from Firebase
      await signOut(firebaseAuth)
    } catch (error) {
      console.error('Logout error:', error)

      // Force clear state even if Firebase signOut fails
      state.user = null
      state.jwtData = null
      state.lastTokenRefresh = null
    } finally {
      state.isLoggingOut = false
    }
  }

  const refreshToken = async () => {
    if (!state.user) return null

    const token = await state.user.getIdToken(true)
    state.jwtData = await state.user.getIdTokenResult()

    return token
  }

  const getValidToken = async () => {
    if (!state.user) return null

    try {
      return await state.user.getIdToken()
    } catch (error) {
      console.error('Failed to get valid token:', error)

      return null
    }
  }

  const hasRole = roles => {
    const userClaims = state.jwtData?.claims?.custom_claims || []
    roles = Array.isArray(roles) ? roles : [roles]

    return roles.some(role => userClaims.includes(role))
  }

  return {
    state,

    login,
    logout,
    hasRole,
    refreshToken,
    getValidToken,
  }
}
